import { useState, useEffect, useRef } from 'react';
import { Download, Share2, X } from 'lucide-react';

const STORAGE_KEY = 'mestly_pwa_dismissed';

interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

export function PWAInstallPrompt() {
  const [visible, setVisible]   = useState(false);
  const [isIos, setIsIos]       = useState(false);
  const deferredPrompt          = useRef<BeforeInstallPromptEvent | null>(null);

  useEffect(() => {
    // Never show on desktop or if already dismissed
    if (window.innerWidth >= 768) return;
    if (localStorage.getItem(STORAGE_KEY) === 'true') return;

    // Detect standalone mode (already installed)
    const isStandalone =
      window.matchMedia('(display-mode: standalone)').matches ||
      ('standalone' in window.navigator && (window.navigator as { standalone?: boolean }).standalone === true);
    if (isStandalone) return;

    const ios = /iphone|ipad|ipod/i.test(navigator.userAgent);
    setIsIos(ios);

    if (ios) {
      // iOS doesn't fire beforeinstallprompt — show instructions directly
      setVisible(true);
      return;
    }

    const handler = (e: Event) => {
      e.preventDefault();
      deferredPrompt.current = e as BeforeInstallPromptEvent;
      setVisible(true);
    };

    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  const dismiss = () => {
    localStorage.setItem(STORAGE_KEY, 'true');
    setVisible(false);
  };

  const install = async () => {
    if (!deferredPrompt.current) return;
    await deferredPrompt.current.prompt();
    const { outcome } = await deferredPrompt.current.userChoice;
    if (outcome === 'accepted') {
      localStorage.setItem(STORAGE_KEY, 'true');
    }
    deferredPrompt.current = null;
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div
      style={{
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        zIndex: 50,
        background: '#ffffff',
        borderTop: '1px solid #e8f4f2',
        boxShadow: '0 -4px 16px rgba(0,0,0,0.08)',
        padding: '14px 16px',
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        fontFamily: "'Montserrat', sans-serif",
      }}
    >
      {/* Icon */}
      <div
        style={{
          width: 40, height: 40, borderRadius: 10, flexShrink: 0,
          background: '#165568',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}
      >
        {isIos
          ? <Share2 size={20} color="#ffffff" />
          : <Download size={20} color="#ffffff" />
        }
      </div>

      {/* Text */}
      <div style={{ flex: 1, minWidth: 0 }}>
        {isIos ? (
          <>
            <p style={{ margin: 0, fontSize: 13, fontWeight: 600, color: '#165568' }}>
              Instala Mestly en tu iPhone
            </p>
            <p style={{ margin: 0, fontSize: 12, color: '#666', marginTop: 2 }}>
              Toca <strong>Compartir</strong> y luego <strong>"Agregar a pantalla de inicio"</strong>
            </p>
          </>
        ) : (
          <>
            <p style={{ margin: 0, fontSize: 13, fontWeight: 600, color: '#165568' }}>
              Instala Mestly en tu teléfono
            </p>
            <p style={{ margin: 0, fontSize: 12, color: '#666', marginTop: 2 }}>
              Accede más rápido sin abrir el navegador
            </p>
          </>
        )}
      </div>

      {/* Actions */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
        {!isIos && (
          <button
            onClick={install}
            style={{
              background: '#2db1c3', color: '#ffffff',
              border: 'none', borderRadius: 8,
              padding: '8px 14px', fontSize: 13, fontWeight: 600,
              cursor: 'pointer', fontFamily: "'Montserrat', sans-serif",
            }}
          >
            Instalar
          </button>
        )}
        <button
          onClick={dismiss}
          aria-label="Cerrar"
          style={{
            background: 'none', border: 'none', cursor: 'pointer',
            padding: 4, display: 'flex', alignItems: 'center',
            color: '#999',
          }}
        >
          <X size={18} />
        </button>
      </div>
    </div>
  );
}
